/**
 * canvas-engine/renderer/elements/GridElement.jsx
 *
 * OWN per-type element renderer for `grid` elements.
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 *
 * Inner-content vs positioning CONTRACT: renders ONLY the inner grid, filling
 * its positioned box. The parent CanvasRenderer owns positioning. DOM output is
 * identical in builder and reader modes. (Req 4.1, 16.1)
 *
 * Lays out `content.items` in `layout.columns` columns / `layout.gap` gap; each
 * item has title/description/image (resolved via OWN `assetRef`). Pure.
 */

import React from 'react';
import { assetRef } from '../../model/assetRef';

function str(v) {
  return v == null ? '' : String(v);
}

function color(v, fallback) {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

/**
 * Render a grid of item cards.
 *
 * @param {object} props
 * @param {object} props.element normalized `grid` Canvas_Element
 */
export default function GridElement({ element }) {
  const content = (element && element.content) || {};
  const style = (element && element.style) || {};
  const layout = (element && element.layout) || {};

  const items = Array.isArray(content.items) ? content.items : [];
  const columns = Math.max(1, num(layout.columns, 3));
  const gap = num(layout.gap, 14);
  const imageRadius = num(style.imageRadius, 12);
  const objectFit = typeof style.objectFit === 'string' ? style.objectFit : 'cover';

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap,
    overflow: 'hidden',
  };

  return (
    <div className="ce-grid" style={rootStyle}>
      {items.map((item, index) => {
        const it = item && typeof item === 'object' ? item : {};
        const itemStyle = it.style && typeof it.style === 'object' ? it.style : {};
        const asset = assetRef(it.image != null ? it.image : it.asset);
        const imageSrc = asset.displayLink || asset.storedLink || '';
        const backgroundAsset = assetRef(itemStyle.backgroundImage || it.backgroundImage);
        const backgroundImage = backgroundAsset.displayLink || backgroundAsset.storedLink || '';
        const title = str(it.title);
        const description = str(it.description);
        const key = str(it.id) || `grid-item-${index}`;
        const cardStyle = {
          display: 'flex',
          flexDirection: 'column',
          gap: num(itemStyle.gap, 8),
          minWidth: 0,
          color: color(itemStyle.textColor, color(style.textColor, '#0f172a')),
          background: color(itemStyle.backgroundColor, color(it.backgroundColor, color(style.cardBackground, 'transparent'))),
          border: itemStyle.borderColor || style.cardBorderColor
            ? `1px solid ${color(itemStyle.borderColor, color(style.cardBorderColor, '#e2e8f0'))}`
            : '0 solid transparent',
          borderRadius: num(itemStyle.borderRadius, num(style.cardRadius, 0)),
          padding: itemStyle.padding != null ? itemStyle.padding : (style.cardPadding || 0),
          boxSizing: 'border-box',
          overflow: backgroundImage ? 'hidden' : undefined,
        };
        if (backgroundImage) {
          cardStyle.backgroundImage = `url(${backgroundImage})`;
          cardStyle.backgroundSize = 'cover';
          cardStyle.backgroundPosition = 'center';
          cardStyle.backgroundRepeat = 'no-repeat';
        }

        return (
          <div
            key={key}
            className="ce-grid__item"
            style={cardStyle}
          >
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={title}
                style={{ width: '100%', height: 140, objectFit, borderRadius: imageRadius, display: 'block' }}
              />
            ) : null}
            {title ? (
              <span
                className="ce-grid__title"
                style={{
                  fontSize: num(itemStyle.titleSize, num(style.titleSize, 16)),
                  fontWeight: itemStyle.titleWeight || style.titleWeight || 600,
                  color: color(itemStyle.titleColor, color(itemStyle.textColor, color(style.titleColor, '#0f172a'))),
                }}
              >
                {title}
              </span>
            ) : null}
            {description ? (
              <span
                className="ce-grid__description"
                style={{
                  fontSize: num(itemStyle.descriptionSize, num(style.descriptionSize, 13)),
                  lineHeight: itemStyle.lineHeight || style.lineHeight || 1.5,
                  color: color(itemStyle.descriptionColor, color(itemStyle.textColor, color(style.descriptionColor, '#475569'))),
                }}
              >
                {description}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
